import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import Banner from "@/components/Banner";
import { useAuth } from "@/context/AuthContext";
import { hasAction } from "@/lib/rbac";
import CandidateFormModal from "@/components/CandidateFormModal";
import CandidateHireModal from "@/components/CandidateHireModal";
import {
  ArrowLeft,
  User,
  Phone,
  Briefcase,
  FileText,
  FileSignature,
  GraduationCap,
  Pencil,
  UserCheck,
  MoreVertical,
  Download,
  Upload,
} from "lucide-react";

const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-700 border-slate-200",
  Offered: "bg-sky-100 text-sky-800 border-sky-200",
  Accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Hired: "bg-[#E8F0FF] text-[#226DB4] border-[#226DB4]/30",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Withdrawn: "bg-amber-100 text-amber-800 border-amber-200",
  "No-show": "bg-orange-100 text-orange-800 border-orange-200",
};

const OPEN_STATUSES = new Set(["Draft", "Offered", "Accepted"]);

const TABS = [
  { id: "overview", label: "Overview", icon: User },
  { id: "personal", label: "Personal", icon: Phone },
  { id: "offer", label: "Offer / Job", icon: Briefcase },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "letters", label: "Letters", icon: FileSignature },
];

function displayName(c) {
  return [c?.first_name, c?.middle_name, c?.last_name].filter(Boolean).join(" ");
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-800">{value || "—"}</div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`border border-slate-200 rounded-lg p-4 ${className}`}>{children}</div>;
}

export default function CandidateProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasAction(user, "candidates", "edit") || user?.role === "admin";
  const canLetters = hasAction(user, "letters", "add") || hasAction(user, "letters", "view") || user?.role === "admin";

  const [candidate, setCandidate] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [letters, setLetters] = useState([]);
  const [tab, setTab] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    if (!id) return;
    setErr("");
    try {
      const [cand, deps, desigs, locs, cos, emps, lets] = await Promise.all([
        api.get(`/candidates/${id}`),
        api.get("/departments"),
        api.get("/designations"),
        api.get("/locations").catch(() => ({ data: [] })),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/letters", { params: { candidate_id: id } }).catch(() => ({ data: [] })),
      ]);
      setCandidate(cand.data);
      setDepartments(deps.data || []);
      setDesignations(desigs.data || []);
      setLocations((locs.data || []).filter((l) => l.is_active !== false));
      setCompanies(cos.data || []);
      setEmployees((emps.data || []).filter((e) => e.employment?.status !== "Inactive"));
      setLetters(lets.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load candidate");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const depMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const desigMap = useMemo(() => Object.fromEntries(designations.map((d) => [d.id, d])), [designations]);
  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);

  if (!candidate) {
    return (
      <AdminLayout title="Candidate Profile" breadcrumbs={["Home", "Pre-hire", "Candidates"]} accent="prehire">
        <div className="p-12 text-center text-slate-400">{err || "Loading…"}</div>
      </AdminLayout>
    );
  }

  const name = displayName(candidate);
  const isOpen = OPEN_STATUSES.has(candidate.status);

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name || "Document");
      fd.append("company_id", candidate.company_id || "company");
      await api.post(`/candidates/${candidate.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOkMsg("Document uploaded");
      await load();
      setTimeout(() => setOkMsg(""), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(
        `/candidates/${candidate.id}/documents/${doc.id}/download`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = doc.file_name || doc.name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setErr("Download failed");
    }
  };

  return (
    <AdminLayout title={name} breadcrumbs={["Home", "Pre-hire", "Candidates", candidate.code]} accent="prehire">
      <Banner variant="error" onClose={() => setErr("")}>{err}</Banner>
      <Banner variant="success" onClose={() => setOkMsg("")}>{okMsg}</Banner>

      <div className="space-y-4" data-testid="candidate-profile-page">
        <div className="bg-gradient-to-r from-sky-50/90 to-white rounded-lg shadow border border-sky-200/70 p-5 flex items-start gap-4">
          <button type="button" onClick={() => navigate("/candidates")} className="p-2 text-slate-500 hover:bg-sky-100/80 rounded" data-testid="profile-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-16 w-16 rounded-full bg-sky-600 text-white flex items-center justify-center font-semibold text-xl uppercase">
            {candidate.first_name?.[0] || "C"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-slate-800">{name}</h2>
              <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[candidate.status] || "bg-slate-100"}`}>
                {candidate.status}
              </span>
              {candidate.intended_employment_type && (
                <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">{candidate.intended_employment_type}</span>
              )}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {candidate.code} · {desigMap[candidate.designation_id]?.title || "—"} · {depMap[candidate.department_id]?.name || "—"}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {[candidate.email, candidate.phone].filter(Boolean).join(" · ") || "—"}
            </div>
            {candidate.hired_employee_id && (
              <Link to={`/employees/${candidate.hired_employee_id}`} className="inline-block mt-2 text-xs text-[#226DB4] hover:underline">
                Open hired employee →
              </Link>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded border border-slate-200"
              data-testid="profile-actions-menu"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-10 w-56 bg-white border border-slate-200 rounded shadow-lg z-10">
                {canEdit && candidate.status !== "Hired" && (
                  <button type="button" onClick={() => { setMenuOpen(false); setEditOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                    <Pencil className="h-3.5 w-3.5" /> Edit Candidate
                  </button>
                )}
                {canLetters && isOpen && (
                  <button type="button" onClick={() => { setMenuOpen(false); navigate(`/letters?action=generate&candidate_id=${candidate.id}`); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                    <FileSignature className="h-3.5 w-3.5" /> Issue Offer Letter
                  </button>
                )}
                {canEdit && isOpen && (
                  <button type="button" onClick={() => { setMenuOpen(false); setHireOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                    <UserCheck className="h-3.5 w-3.5" /> Hire
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-sky-200/70">
          <div className="flex border-b border-sky-100 bg-sky-50/30 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition whitespace-nowrap ${
                  tab === t.id ? "border-sky-600 text-sky-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "overview" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card><Field label="Status" value={candidate.status} /></Card>
                  <Card><Field label="Employment Type" value={candidate.intended_employment_type} /></Card>
                  <Card><Field label="Joining Date" value={formatDisplayDate(candidate.proposed_joining_date)} /></Card>
                  <Card><Field label="Offer Date" value={formatDisplayDate(candidate.offer_date)} /></Card>
                </div>
                <Card>
                  <h4 className="text-sm font-semibold text-slate-800 mb-3">At a glance</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Field label="Department" value={depMap[candidate.department_id]?.name} />
                    <Field label="Designation" value={desigMap[candidate.designation_id]?.title} />
                    <Field label="Work Location" value={candidate.work_location} />
                    <Field label="Annual CTC / Stipend" value={candidate.annual_ctc ? `₹ ${Number(candidate.annual_ctc).toLocaleString("en-IN")}` : ""} />
                    <Field label="Official Email" value={candidate.email} />
                    <Field label="Phone" value={candidate.phone} />
                  </div>
                </Card>
                {candidate.notes && (
                  <Card>
                    <Field label="Notes" value={candidate.notes} />
                  </Card>
                )}
              </div>
            )}

            {tab === "personal" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="First Name" value={candidate.first_name} />
                <Field label="Middle Name" value={candidate.middle_name} />
                <Field label="Last Name" value={candidate.last_name} />
                <Field label="Gender" value={candidate.gender} />
                <Field label="Date of Birth" value={formatDisplayDate(candidate.date_of_birth)} />
                <Field label="Official Email" value={candidate.email} />
                <Field label="Personal Email" value={candidate.personal_email} />
                <Field label="Phone" value={candidate.phone} />
                <Field label="PAN" value={candidate.pan} />
                <Field label="Aadhaar" value={candidate.aadhaar} />
                <Field label="Address Line 1" value={candidate.address_line1} />
                <Field label="Address Line 2" value={candidate.address_line2} />
                <Field label="City" value={candidate.city} />
                <Field label="State" value={candidate.state} />
                <Field label="Country" value={candidate.country} />
                <Field label="Pincode" value={candidate.pincode} />
              </div>
            )}

            {tab === "offer" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Employment Type" value={candidate.intended_employment_type} />
                <Field label="Status" value={candidate.status} />
                <Field label="Department" value={depMap[candidate.department_id]?.name} />
                <Field label="Designation" value={desigMap[candidate.designation_id]?.title} />
                <Field label="Joining Date" value={formatDisplayDate(candidate.proposed_joining_date)} />
                <Field label="Offer Date" value={formatDisplayDate(candidate.offer_date)} />
                <Field label="Work Location" value={candidate.work_location} />
                <Field label="Branch / Location" value={locMap[candidate.location_id]?.name || locMap[candidate.location_id]?.code} />
                <Field label="Annual CTC / Stipend" value={candidate.annual_ctc ? `₹ ${Number(candidate.annual_ctc).toLocaleString("en-IN")}` : ""} />
                <Field label="Stipend Note" value={candidate.stipend_note} />
                <Field label="Company" value={(companies.find((c) => c.id === candidate.company_id) || {}).name || candidate.company_id} />
              </div>
            )}

            {tab === "education" && (
              (!candidate.education || candidate.education.length === 0) ? (
                <div className="text-sm text-slate-400">No education records.</div>
              ) : (
                <div className="border border-slate-200 rounded divide-y divide-slate-100">
                  {candidate.education.map((ed, i) => (
                    <div key={ed.id || i} className="px-3 py-2 text-sm">
                      <div className="font-medium text-slate-800">{ed.qualification || "—"}</div>
                      <div className="text-xs text-slate-500">
                        {[ed.institution, ed.year, ed.specialization].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {tab === "documents" && (
              <div className="space-y-3">
                {canEdit && candidate.status !== "Hired" && (
                  <label className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-slate-200 rounded cursor-pointer hover:bg-slate-50">
                    <Upload className="h-3.5 w-3.5 text-[#226DB4]" />
                    {uploading ? "Uploading…" : "Upload document"}
                    <input
                      type="file"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadDoc(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
                {(!candidate.documents || candidate.documents.length === 0) ? (
                  <div className="text-sm text-slate-400">No documents uploaded.</div>
                ) : (
                  <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">File</th>
                        <th className="px-3 py-2">Uploaded</th>
                        <th className="px-3 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidate.documents.map((d) => (
                        <tr key={d.id} className="border-t border-slate-100">
                          <td className="px-3 py-2">{d.name || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{d.file_name || "—"}</td>
                          <td className="px-3 py-2 text-slate-500 text-xs">{formatDisplayDate(d.uploaded_at)}</td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => downloadDoc(d)} className="p-1.5 text-[#226DB4] hover:bg-[#E8F0FF] rounded">
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === "letters" && (
              letters.length === 0 ? (
                <div className="text-sm text-slate-400">
                  No letters issued yet.
                  {canLetters && isOpen && (
                    <button
                      type="button"
                      className="ml-2 text-[#226DB4] hover:underline"
                      onClick={() => navigate(`/letters?action=generate&candidate_id=${candidate.id}`)}
                    >
                      Issue Offer Letter
                    </button>
                  )}
                </div>
              ) : (
                <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Letter No.</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Issued By</th>
                      <th className="px-3 py-2 text-right">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {letters.map((l) => (
                      <tr key={l.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{formatDisplayDate(l.issue_date)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{l.letter_number || "—"}</td>
                        <td className="px-3 py-2">{l.type}</td>
                        <td className="px-3 py-2 text-slate-600">{l.issued_by_name || "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            className="p-1.5 text-[#226DB4] hover:bg-[#E8F0FF] rounded"
                            onClick={async () => {
                              const res = await api.get(`/exports/letter/${l.id}.pdf`, { responseType: "blob" });
                              const blob = new Blob([res.data]);
                              const link = document.createElement("a");
                              link.href = window.URL.createObjectURL(blob);
                              link.download = `${(l.type || "letter").replace(/ /g, "_")}_${l.employee_code || "cand"}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                            }}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      </div>

      {editOpen && (
        <CandidateFormModal
          editing={candidate}
          companies={companies}
          departments={departments}
          designations={designations}
          locations={locations}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            setOkMsg("Candidate updated");
            await load();
            setTimeout(() => setOkMsg(""), 2500);
          }}
        />
      )}

      {hireOpen && (
        <CandidateHireModal
          candidate={candidate}
          departments={departments}
          designations={designations}
          employees={employees}
          onClose={() => setHireOpen(false)}
          onDone={async (res) => {
            setHireOpen(false);
            if (res?.employee_id) navigate(`/employees/${res.employee_id}`);
            else await load();
          }}
        />
      )}
    </AdminLayout>
  );
}
